/**
 * NOS-159: Stock photo picker (Unsplash) for the builder.
 * Dark-themed overlay dialog: search box, thumbnail grid with photographer
 * attribution (per Unsplash guidelines), load-more pagination. Selecting a
 * photo ingests it into org storage (never hot-linked) and calls onSelect
 * with the storage URL.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import {
  searchStockImages,
  ingestStockImage,
  type StockPhoto,
} from "@/lib/stockImages"

export interface StockImageDialogProps {
  orgId: string
  open: boolean
  onClose: () => void
  /** Called with the org-storage URL (and alt text) after ingest completes. */
  onSelect: (url: string, alt: string) => void | Promise<void>
  /** Pre-filled search, e.g. from a theme's unsplash_queries. */
  initialQuery?: string
}

export function StockImageDialog({ orgId, open, onClose, onSelect, initialQuery }: StockImageDialogProps) {
  const [query, setQuery] = useState(initialQuery ?? "")
  const [results, setResults] = useState<StockPhoto[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [searching, setSearching] = useState(false)
  const [ingestingId, setIngestingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const activeQuery = useRef("")

  const runSearch = useCallback(
    async (q: string, nextPage: number, append: boolean) => {
      if (!q.trim()) return
      setSearching(true)
      setError(null)
      activeQuery.current = q
      try {
        const res = await searchStockImages(orgId, q, nextPage)
        if (activeQuery.current !== q) return
        setResults((prev) => (append ? [...prev, ...res.results] : res.results))
        setPage(res.page)
        setTotalPages(res.total_pages)
      } catch (e) {
        setError(e instanceof Error ? e.message : "Search failed")
        if (!append) setResults([])
      } finally {
        setSearching(false)
      }
    },
    [orgId]
  )

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
      if (initialQuery?.trim() && results.length === 0) {
        void runSearch(initialQuery, 1, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    if (open) document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open) return null

  const handlePick = async (photo: StockPhoto) => {
    if (ingestingId) return
    setIngestingId(photo.id)
    try {
      const ingested = await ingestStockImage(orgId, photo)
      await onSelect(ingested.url, ingested.alt)
      toast.success("Stock photo added")
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not add the stock image")
    } finally {
      setIngestingId(null)
    }
  }

  return (
    <div
      data-testid="stock-image-dialog"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, calc(100vw - 48px))",
          maxHeight: "min(640px, calc(100vh - 48px))",
          display: "flex",
          flexDirection: "column",
          background: "rgba(18,18,24,0.98)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", gap: 8, padding: 14, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <input
            ref={inputRef}
            data-testid="stock-search-input"
            placeholder="Search free photos — try “volunteers”, “community garden”…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runSearch(query, 1, false)
            }}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 13,
              color: "#fff",
              outline: "none",
            }}
          />
          <button
            type="button"
            data-testid="stock-search-btn"
            onClick={() => void runSearch(query, 1, false)}
            disabled={searching || !query.trim()}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              cursor: searching ? "default" : "pointer",
              opacity: searching || !query.trim() ? 0.5 : 1,
            }}
          >
            {searching && results.length === 0 ? "Searching…" : "Search"}
          </button>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              color: "rgba(255,255,255,0.5)",
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          {error ? (
            <div style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
              {error}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 13 }}>
              {searching ? "Searching…" : "Search Unsplash for free, high-quality photos."}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 10,
                }}
              >
                {results.map((photo) => (
                  <figure key={photo.id} style={{ margin: 0 }}>
                    <button
                      type="button"
                      data-testid={`stock-photo-${photo.id}`}
                      onClick={() => void handlePick(photo)}
                      disabled={ingestingId !== null}
                      title={photo.alt}
                      style={{
                        display: "block",
                        width: "100%",
                        aspectRatio: "4 / 3",
                        padding: 0,
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: 8,
                        overflow: "hidden",
                        cursor: ingestingId ? "default" : "pointer",
                        background: "rgba(255,255,255,0.03)",
                        opacity: ingestingId && ingestingId !== photo.id ? 0.4 : 1,
                        position: "relative",
                      }}
                    >
                      <img
                        src={photo.thumb_url}
                        alt={photo.alt}
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      {ingestingId === photo.id && (
                        <span
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Adding…
                        </span>
                      )}
                    </button>
                    <figcaption style={{ marginTop: 3, fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                      <a
                        href={`${photo.photographer_url}?utm_source=nos&utm_medium=referral`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "inherit", textDecoration: "none" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {photo.photographer_name}
                      </a>{" "}
                      · Unsplash
                    </figcaption>
                  </figure>
                ))}
              </div>
              {page < totalPages && (
                <div style={{ textAlign: "center", marginTop: 14 }}>
                  <button
                    type="button"
                    data-testid="stock-load-more"
                    onClick={() => void runSearch(query, page + 1, true)}
                    disabled={searching}
                    style={{
                      padding: "8px 20px",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.1)",
                      background: "transparent",
                      color: "rgba(255,255,255,0.7)",
                      fontSize: 12,
                      cursor: searching ? "default" : "pointer",
                    }}
                  >
                    {searching ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: "8px 14px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            fontSize: 10,
            color: "rgba(255,255,255,0.3)",
          }}
        >
          Photos from Unsplash. Selected photos are saved to your media storage — nothing is hot-linked.
        </div>
      </div>
    </div>
  )
}
